import { computed, createContext, customRef, getAccountStorage, mapProp, model, Model, modelAction, pickSyncedKeys, prop, Ref } from '@weavix/mobx';
import { isEqual, sortBy } from 'lodash';
import { observable, when } from 'mobx';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { myUserId } from '../my-profile-store/my-profile-store';
import { Channel } from './channel';
import { ChannelMessage } from './channel-message';
import { ChannelType } from '@weavix/models/src/channel/channel';
import { SyncService } from '@weavix/sync';
import { coerceToDate } from '@weavix/utils/src/coerce-to-date';
import { ChannelMessageArchiver } from './channel-message-archiver';
import { MessageAttachment } from '@weavix/models/src/channel/channel-message';

export const channelRef = customRef<Channel>((id: string) => channelsContext.get()?.getChannelById(id));

export const channelsContext = createContext<ChannelsStore>();

/**
 * How far `userId` has read: the server's position (channel or preference); the open channel counts as read to its sequence,
 * but never below the server's position, since sync updates for a send can arrive in any order and leave that sequence stale.
 */
export function channelReadSequence(
    channel: { sequence?: number, readSequences?: Record<string, number> },
    preference: { readSequence?: number } | undefined,
    userId: string | undefined,
    isOpen: boolean,
): number | undefined {
    const mine = userId ? channel.readSequences?.[userId] : undefined;
    const serverRead = Math.max(mine ?? 0, preference?.readSequence ?? 0);
    if (!isOpen) return serverRead;
    if (channel.sequence === undefined) return channel.sequence;
    return Math.max(channel.sequence, serverRead);
}

/** A channel as the `channels` sync store holds it; only the fields the store reads are named. */
type SyncedChannel = {
    sequence?: number,
    readSequences?: Record<string, number>,
    pendingPeople?: unknown[],
    partition?: unknown,
    updated?: unknown,
};

/** A channel preference as the `channel-preferences` sync store holds it; only the fields the store reads are named. */
type SyncedPreference = {
    readSequence?: number,
    notification?: unknown,
    favoriteIndex?: number,
    isSnoozed?: boolean,
    updated?: unknown,
};

/**
 * The record the store keeps for one synced channel: its synced fields, its last message, and `userId`'s read position and
 * preferences laid over them; `null` while the channel has no record yet or still waits on its people.
 */
export function conversationRecord(
    channel: SyncedChannel | undefined,
    preference: SyncedPreference | undefined,
    message: ChannelMessage | undefined,
    userId: string | undefined,
    isOpen: boolean,
): Channel | null {
    if (!channel || channel.pendingPeople?.includes(channel.partition)) return null;

    return {
        ...channel,
        lastMessage: message ? new ChannelMessage(message) : null,
        readSequence: channelReadSequence(channel, preference, userId, isOpen),
        notificationPreference: preference?.notification,
        favoriteIndex: preference?.favoriteIndex,
        isSnoozed: preference?.isSnoozed,
        updated: preference?.updated ?? channel.updated,
    } as unknown as Channel;
}

@model('ChannelsStore')
export class ChannelsStore extends Model({
    focusedChannel: prop<Ref<Channel> | undefined>(),
    selectedChannel: prop<Ref<Channel> | undefined>(),
    channelsMap: mapProp<string, Channel>(),
}) {
    loaded: Promise<any>;
    close: () => any;
    readonly newMessage$ = new Subject<ChannelMessage>();
    private readonly temporaryChannelsMap = observable.map<string, Channel>();
    private readonly lastMessages = new Map<string, ChannelMessage>();
    private readonly channelMessageArchiver = new ChannelMessageArchiver();

    @modelAction
    setFocusedChannel(channelId?: string) {
        this.focusedChannel = channelId ? channelRef(channelId) : undefined;
    }

    @modelAction
    setSelectedChannel(channelId?: string) {
        this.selectedChannel = channelId ? channelRef(channelId) : undefined;
    }

    get selectedChannelId() {
        return this.selectedChannel?.id;
    }

    get focusedChannelId() {
        return this.focusedChannel?.id;
    }

    constructor(topic?: string, messageTopic?: string, preferenceTopic?: string) {
        super();
        channelsContext.setDefault(this);
        const preferences = new Map<string, any>();
        const channels = new Map<string, any>();

        const getConversation = (channelId: string) => conversationRecord(
            channels.get(channelId),
            preferences.get(channelId),
            this.lastMessages.get(channelId),
            myUserId(),
            this.selectedChannel?.id === channelId,
        );

        const refreshAll = () => {
            const sorted = [...channels.values()].sort((a, b) => {
                return (a.updated ?? '') < (b.updated ?? '') ? -1 : 1;
            });
            const records = sorted
                .map(x => x.id)
                .map(getConversation)
                .filter(Boolean) as Channel[];
            this.setConversations(records);
        };

        const refresh = (channelId: string, keys?: string[]) => {
            const record = getConversation(channelId);
            if (record) this.updatedConversation(record, keys);
            else this.deleteConversation(channelId);
        };

        let lastChannelId = '';
        const indexPredicate = {
            isMatch: (index: string) => {
                const channelId = index?.split('|')[0];
                if (!channelId || channelId === lastChannelId) return false;
                lastChannelId = channelId;
                return true;
            },
            reset: () => {
                lastChannelId = '';
            },
        };

        const messageOptions = { name: 'channel-messages', topics: messageTopic, readonly: false };
        const preferenceOptions = { name: 'channel-preferences', topics: preferenceTopic, readonly: !!preferenceTopic };
        const channelOptions = { name: 'channels', topics: topic, readonly: !!topic };
        const emitters = [
            getAccountStorage<ChannelMessage>(messageOptions).Instance()
                .query({
                    index: indexPredicate,
                })
                .on('load', values => {
                    values.forEach(message => {
                        this.lastMessages.set(message.channelId, message);
                    });
                    refreshAll();
                    indexPredicate.reset();
                })
                .on('update', ({ value }) => {
                    this.newMessage$.next(new ChannelMessage(value));
                    const existing = this.lastMessages.get(value.channelId);
                    if (!existing || new Date(existing.date) <= new Date(value.date)) {
                        this.lastMessages.set(value.channelId, value);
                        refresh(value.channelId, ['lastMessage']);
                        indexPredicate.reset();
                    }
                }),
            getAccountStorage<any>(preferenceOptions).Instance()
                .query()
                .on('load', values => {
                    values.forEach(preference => {
                        preferences.set(preference.id, preference);
                    });
                    refreshAll();
                })
                .on('update', ({ value, keys }) => {
                    const index = keys?.indexOf('notification');
                    if (index >= 0) keys[index] = 'notificationPreference';
                    preferences.set(value.id, value);
                    refresh(value.id, keys);
                }),
            getAccountStorage<Channel>(channelOptions).Instance()
                .query()
                .on('load', async values => {
                    const start = Date.now();
                    channels.clear();
                    values.forEach(value => channels.set(value.id, value));
                    await Promise.all(values.map(value => this.archiveChannelMessages(value.id, coerceToDate(this.getArchived(value)), messageTopic)));
                    refreshAll();
                    console.log(`Loaded channels ${this.channels.length} in ${Date.now() - start} ms`);
                })
                .on('delete', value => {
                    channels.delete(value.id);
                    refresh(value.id);
                    this.deleteChannelMessages(value.id);
                })
                .on('update', async ({ value, keys }) => {
                    if (keys?.includes('archived')) {
                        await this.archiveChannelMessages(value.id, coerceToDate(this.getArchived(value)), messageTopic);
                        refresh(value.id, ['lastMessage']);
                    }
                    channels.set(value.id, value);
                    refresh(value.id, keys);
                }),
        ];
        Object.defineProperty(this, 'loaded', { get: () => Promise.all(emitters.map(x => x.wait())) });
        Object.defineProperty(this, 'close', () => {
            getAccountStorage<any>(preferenceOptions).Instance().close();
            getAccountStorage<any>(messageOptions).Instance().close();
            getAccountStorage<any>(channelOptions).Instance().close();
        });
    }

    async deleteChannelMessages(channelId: string) {
        const store = getAccountStorage<any>('channel-messages').Instance();
        await store.index.removeMany({
            isMatch: (_id, index) => index?.startsWith(channelId),
        });
    }

    async archiveChannelMessages(channelId: string, archived: Date, messageTopic: string) {
        const archivedMessageIds = await this.channelMessageArchiver.archiveChannelMessages(channelId, archived, messageTopic);
        if (archivedMessageIds.has(this.lastMessages.get(channelId)?.id)) {
            this.lastMessages.delete(channelId);
        }
    }

    @computed
    get channels() {
        return [...this.channelsMap.values()];
    }

    @computed
    get channelsSortedFirstRecent() {
        const withoutAi = this.channels.filter(x => !x.isAiVoiceAssistantChannel);
        return sortBy([...withoutAi, ...this.temporaryChannelsMap.values()],
            x => x.lastMessage ? x.lastMessage.date.toISOString() : x.created).reverse();
    }

    get unreadCount() {
        return this.channels.reduce((o, v) => o + v.unreadCount, 0);
    }

    @modelAction
    deleteConversation(id: string) {
        this.channelsMap.delete(id);
    }

    @modelAction
    updatedConversation(conversation: Channel, keys?: string[]) {
        if (this.channelsMap.has(conversation.id)) this.channelsMap.get(conversation.id)?.update(keys ? pickSyncedKeys(conversation, keys) : conversation);
        else this.channelsMap.set(conversation.id, new Channel(conversation));
    }

    @modelAction
    setConversations(conversations: Channel[]) {
        this.channelsMap.clear();
        conversations.forEach(v => {
            this.channelsMap.set(v.id, new Channel(v));
        });
    }

    getChannelById(channelId: string) {
        return this.channelsMap.get(channelId) ?? this.temporaryChannelsMap.get(channelId);
    }

    getChannelByPersonIds(personIds: string[]): Channel {
        if (!personIds || personIds.length === 0) return;
        personIds = [...new Set(personIds)].sort();
        const channel = this.channelsSortedFirstRecent
            .filter(x => x.type === ChannelType.People)
            .filter(x => x.people?.length === personIds.length)
            .find(x => isEqual(x.people?.sort(), personIds));
        return channel;
    }

    async createGroup(people: string[], id?: string) {
        const channel = await SyncService.instance.post<Channel>('/core/channels/groups', { people, id });
        await when(() => this.channelsMap.has(channel.id), { timeout: 40_000 });
        return this.channelsMap.get(channel.id);
    }

    async createTemporary(people: string[]) {
        this.clearTemporary();
        people = [...new Set(people.concat([myUserId()]))].sort();

        if (people.length !== 1) {
            // if an existing group with two people used to have more than two people, don't reuse it
            const existing = this.channels.find(channel => channel.type === ChannelType.People && isEqual(channel.people.sort(), people));

            // on web radio, you can select a user you already have a 1-on-1 channel with when creating a new group
            // so we check for existing 1-on-1 channels with the same people
            // we also have to make sure that the channel type is 'people'
            const existing1on1Channel = this.channels.find(channel => channel.type === ChannelType.People && !channel.isTwoUsersInGroupChannel && isEqual(channel.people.sort(), people));

            if (existing && !existing.isTwoUsersInGroupChannel) {
                return existing;
            } else if (existing1on1Channel) {
                return existing1on1Channel;
            }
        }

        const channel = new Channel({ people, type: ChannelType.People, id: uuid(), created: new Date().toISOString() });
        channel.temporary = true;
        this.temporaryChannelsMap.set(channel.id, channel);
        return channel;
    }

    async ensureCreated(temporary: Channel) {
        if (temporary.type === ChannelType.Local) return temporary;

        if (!temporary.temporary) return temporary;
        temporary.temporary = false;

        const channel = await this.createGroup(temporary.people, temporary.id);
        // We don't have notification preferences in Web Radio yet. Removing for now.
        // await channel.updateNotification(temporary.notification);
        this.temporaryChannelsMap.delete(temporary.id);
        temporary.update(channel); // Temporary channel assumes new ID and everything (which means new channel gets pulled also)
        return channel;
    }

    clearTemporary() {
        this.temporaryChannelsMap.clear();
    }

    deleteTemporary(tempId: string) {
        this.temporaryChannelsMap.delete(tempId);
    }

    getArchived(channel: Channel): Date | string | undefined {
        const archived = channel.archived;
        if (!archived) return undefined;
        // on ADCs, archived is a single date
        if (typeof archived === 'string' || archived instanceof Date) return archived;
        // on UDCs, archived is a map of userIds to dates
        return archived[myUserId()];
    }

    // Used to handle sending an attachment on new channel creation
    // raw data cannot be stringified to store in localStorage
    // base64 data can be too large to store in localStorage
    // so we store the data temporarily in memory
    private readonly temporaryAttachments = new Map<string, MessageAttachment[]>();
    saveTemporaryAttachments(channelId: string, attachments: MessageAttachment[]) {
        this.temporaryAttachments.set(channelId, attachments);
    }

    getTemporaryAttachments(channelId: string): MessageAttachment[] {
        const attachments = this.temporaryAttachments.get(channelId) || [];
        this.temporaryAttachments.set(channelId, []);
        return attachments;
    }

}
